import api from '@pray/shared/utils/api';
import type { ImportColumnMap, ImportFileKind, PaymentsImportFileKind, QboImportFileKind } from './getImportDefaults';

export type ImportRowError = {
  row_index: number;
  field: string;
  message: string;
  raw_value?: string;
};

export type ImportUnmatchedLeader = {
  row_index: number;
  raw_leader: string;
  normalized: string;
};

export type ImportRowDiff =
  | { status: 'added' }
  | { status: 'unchanged'; existing_artist_uuid: string }
  | {
      status: 'changed';
      existing_artist_uuid: string;
      changed_fields: Array<{ field: string; from: unknown; to: unknown }>;
    };

export type ImportParsedRow = {
  row_index: number;
  raw: Record<string, string>;
  parsed: Record<string, unknown>;
  resolved_artist: {
    internal_id: number | null;
    artist_uuid: string | null;
    db_name: string | null;
    confidence: string;
    alias?: string;
    score?: number;
  };
  diff: ImportRowDiff | null;
};

export type ImportDiffSummary = {
  added: number;
  changed: number;
  unchanged: number;
  errors: number;
  unmatched: number;
};

export type PostImportPreviewResponse = {
  run_id: string;
  // Literal (not the wide ImportFileKind) so ImportPreviewResult is a sound
  // discriminated union on file_kind — the server echoes 'TRACKER_CSV' on this arm.
  file_kind: 'TRACKER_CSV';
  headers: string[];
  proposed_map: ImportColumnMap;
  missing_required: string[];
  sample_rows: ImportParsedRow[];
  diff_summary: ImportDiffSummary;
  errors: ImportRowError[];
  unmatched: ImportUnmatchedLeader[];
  created_at: string;
  expires_at: string;
  file_name?: string;
  file_size_bytes?: number;
};

export type PostImportPreviewParams = {
  file: File;
  fileKind?: ImportFileKind;
  columnMap?: ImportColumnMap;
};

export type QboUnmatchedContract = {
  customer_name: string;
  external_invoice_id: string | null;
  organization_id: number;
  reason: 'no_active_contract' | 'multiple_active_contracts' | 'missing_invoice_number';
  row_index: number;
};

export type QboPendingInvoice = {
  contract_pk: number;
  external_invoice_id: string;
  amount_cents: number;
  issue_date: string | null;
  due_date: string;
};

export type QboImportPreviewResponse = {
  run_id: string;
  // The server echoes file_kind unconditionally on both preview arms; on
  // this arm it is always an XLSX kind. Consumers still discriminate
  // structurally on `unmatched_contract` (see ReviewCommitStage).
  file_kind: QboImportFileKind;
  unmatched: Array<{ customer_name: string; file_kind: string; row_index: number }>;
  invoices: QboPendingInvoice[];
  unmatched_contract: QboUnmatchedContract[];
  errors: ImportRowError[];
  created_at: string;
  expires_at: string;
  file_name?: string;
  file_size_bytes?: number;
};

// Payment match buckets — mirrors the server's reason union on each
// unmatched row.
export type PaymentsUnmatchedReason = 'unmatched_org' | 'unmatched_invoice' | 'ambiguous' | 'duplicate';

export type PaymentMethod = 'ach' | 'card' | 'check' | 'wire';

export type PaymentsPendingRow = {
  row_index: number;
  organization: string;
  invoice_id: string;
  external_invoice_id: string;
  external_payment_id: string;
  amount_cents: number;
  paid_at: string;
  method: PaymentMethod | null;
  memo: string | null;
};

export type PaymentsUnmatchedRow = {
  row_index: number;
  organization: string;
  external_invoice_id: string;
  external_payment_id: string;
  reason: PaymentsUnmatchedReason;
};

// Payments preview errors carry file_name (multi-file safe), unlike the
// CSV/QBO ImportRowError. Kept as a distinct type to mirror the wire exactly.
export type PaymentsImportRowError = {
  file_name: string;
  row_index: number;
  field: string;
  message: string;
  raw_value?: string;
};

export type PaymentsReconciliationWarning = {
  row_index: number;
  external_payment_id: string;
  code: 'overpay' | 'invoice_void' | 'invoice_written_off';
  message: string;
};

export type PaymentsDiffSummary = {
  matched: number;
  unmatched_org: number;
  unmatched_invoice: number;
  ambiguous: number;
  duplicate: number;
  total_rows: number;
  errors: number;
  reconciliation_warnings: number;
};

export type PaymentsImportPreviewResponse = {
  run_id: string;
  // The commit/preview controllers echo file_kind unconditionally on every
  // arm; on this arm it is always 'PAYMENTS'. Consumers discriminate on
  // file_kind (see ReviewCommitStage / CommittedStage).
  file_kind: PaymentsImportFileKind;
  file_name: string;
  file_size_bytes: number;
  headers: string[];
  proposed_map: Record<string, string | null>;
  missing_required: string[];
  sample_rows: Array<Record<string, string | number | null>>;
  pending_payments: PaymentsPendingRow[];
  unmatched: PaymentsUnmatchedRow[];
  reconciliation_warnings: PaymentsReconciliationWarning[];
  diff_summary: PaymentsDiffSummary;
  errors: PaymentsImportRowError[];
  created_at: string;
  expires_at: string;
};

export type ImportPreviewResult = PostImportPreviewResponse | QboImportPreviewResponse | PaymentsImportPreviewResponse;

export const postImportPreview = async ({
  file,
  fileKind = 'TRACKER_CSV',
  columnMap,
}: PostImportPreviewParams): Promise<ImportPreviewResult> => {
  const url = '/web/studio/superfunnel/import/preview';
  const form = new FormData();
  form.append('file', file);
  form.append('file_kind', fileKind);
  if (columnMap) form.append('column_map', JSON.stringify(columnMap));
  const response = await api.executePostRequest(url, form);
  return (response.data as { data: ImportPreviewResult }).data;
};
